'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["contextualNodeAttributes", "disabled"];
import { BoldIcon, CodeIcon, ItalicIcon, StrikeIcon, SubscriptIcon, SuperscriptIcon, UnderlineIcon } from 'foundations-assets';
import tinymce from 'tinymce';
import { BLOCK_FORMAT_NODES, FORMAT_COMMAND_TO_INHERITED_COMMAND, hsformattingConfig, i18nNameSpace } from 'tinymce-plugins/constants/hsformatting';
import { applyFormat, getSelectedRngHasCustomFont, shouldApplyOverrideFormat } from 'tinymce-plugins/hsformatting/utils';
import { getCombinedCustomFonts, nodeChangeHandlers, overrideInheritedStyles } from 'tinymce-plugins/utils/formatting';
import ControlButton from '../components/toolbar/ControlButton';
import { CANVAS_BOLD, CANVAS_CODE_FORMAT, CANVAS_ITALIC, CANVAS_STRIKETHROUGH, CANVAS_SUBSCRIPT, CANVAS_SUPERSCRIPT, CANVAS_UNDERLINE } from '../constants/toolbar';
import { getHsformattingSettings } from '../utils';
import { i18nWithNameSpace } from '../utils/i18n';
import { jsx as _jsx } from "react/jsx-runtime";
const registerFormatForInheritedStyleOverride = (editor, format) => {
  const inheritedStylesConfig = getHsformattingSettings(editor.settings, hsformattingConfig.OVERRIDE_INHERITED_STYLES_CONFIG) || {};
  const selector = inheritedStylesConfig[format] || [];
  const styles = overrideInheritedStyles[format];
  const nonAffectedSelector = BLOCK_FORMAT_NODES.filter(n => !selector.includes(n)).map(s => `${s} > span`).join(',');
  if (!selector.length) {
    return;
  }
  const exact = format === 'underline';
  editor.formatter.register({
    [FORMAT_COMMAND_TO_INHERITED_COMMAND[format].on]: [{
      selector: selector.join(','),
      styles: {
        [styles.affectedStyles[0]]: '%value'
      },
      inline: 'span',
      exact,
      split: true,
      deep: true,
      remove_similar: true
    }],
    // this is for removal of the disabled style being applied to unsupported elements
    [FORMAT_COMMAND_TO_INHERITED_COMMAND[format].off]: [{
      selector: nonAffectedSelector,
      styles: {
        [styles.affectedStyles[0]]: styles.off
      },
      remove: 'empty'
    }]
  });
};
const i18nKey = i18nWithNameSpace(i18nNameSpace);
const getControlClickHandler = (editor, format, overrideFormats = []) => isActive => {
  applyFormat(editor, format, overrideFormats, isActive);
};
const controls = [{
  controlName: CANVAS_BOLD,
  iconName: 'bold',
  icon: BoldIcon,
  formatName: 'bold',
  tooltip: 'tooltip.bold',
  getClickHandler: getControlClickHandler,
  shortCut: 'meta+b'
}, {
  controlName: CANVAS_ITALIC,
  iconName: 'italic',
  icon: ItalicIcon,
  formatName: 'italic',
  tooltip: 'tooltip.italic',
  getClickHandler: getControlClickHandler,
  shortCut: 'meta+i'
}, {
  controlName: CANVAS_UNDERLINE,
  iconName: 'underline',
  icon: UnderlineIcon,
  formatName: 'underline',
  tooltip: 'tooltip.underline',
  getClickHandler: getControlClickHandler,
  shortCut: 'meta+u'
}, {
  controlName: CANVAS_STRIKETHROUGH,
  iconName: 'strike',
  icon: StrikeIcon,
  formatName: 'strikethrough',
  tooltip: 'tooltip.strikethrough',
  getClickHandler: getControlClickHandler
}, {
  controlName: CANVAS_SUPERSCRIPT,
  iconName: 'superscript',
  icon: SuperscriptIcon,
  formatName: 'superscript',
  tooltip: 'tooltip.superscript',
  getClickHandler: getControlClickHandler
}, {
  controlName: CANVAS_SUBSCRIPT,
  iconName: 'subscript',
  icon: SubscriptIcon,
  formatName: 'subscript',
  tooltip: 'tooltip.subscript',
  getClickHandler: getControlClickHandler
}, {
  controlName: CANVAS_CODE_FORMAT,
  iconName: 'code',
  icon: CodeIcon,
  formatName: 'code',
  tooltip: 'tooltip.codeformat',
  getClickHandler: getControlClickHandler
}];
export function addFormattingControls(editor) {
  const inheritedStylesConfig = getHsformattingSettings(editor.settings, hsformattingConfig.OVERRIDE_INHERITED_STYLES_CONFIG) || {};
  const getIsActive = (formatName, evt) => {
    if (shouldApplyOverrideFormat(editor, inheritedStylesConfig[formatName])) {
      return nodeChangeHandlers[formatName](evt);
    }
    return editor.formatter.match(formatName);
  };
  const getIsDisabled = (disabled, formatName) => {
    const combinedCustomFonts = getCombinedCustomFonts(editor);
    if (!combinedCustomFonts || !combinedCustomFonts.length || !['bold', 'italic'].includes(formatName)) {
      return disabled;
    }
    const hasCustomFont = getSelectedRngHasCustomFont(editor, combinedCustomFonts);
    return hasCustomFont;
  };
  const nodeChangeHandlerList = [];
  controls.forEach(({
    formatName,
    iconName,
    icon,
    tooltip,
    getClickHandler,
    controlName,
    shortCut
  }) => {
    let lastIsActive = false;
    const eventName = `FormatChanged:${formatName}`;
    const formatChangedCallback = (isActive, e) => {
      lastIsActive = isActive;
      editor.fire(eventName, Object.assign({
        isActive
      }, e));
    };
    if (editor.formatter) {
      editor.formatter.formatChanged(formatName, formatChangedCallback);
      registerFormatForInheritedStyleOverride(editor, formatName);
    } else {
      editor.on('init', () => {
        editor.formatter.formatChanged(formatName, formatChangedCallback);
        registerFormatForInheritedStyleOverride(editor, formatName);
      });
    }
    const nodesAffectedByInheritedStyles = inheritedStylesConfig[formatName];
    tinymce.canvas.addButton(editor, controlName, {
      renderControl: props => {
        const {
            contextualNodeAttributes,
            disabled
          } = props,
          rest = _objectWithoutPropertiesLoose(props, _excluded);
        const {
          [controlName]: isActive
        } = contextualNodeAttributes;
        return /*#__PURE__*/_jsx(ControlButton, Object.assign({
          editor: editor,
          "data-test-id": `toggle-format-${iconName}`,
          shortCut: shortCut,
          formatName: formatName,
          iconName: iconName,
          icon: icon,
          tooltip: i18nKey(tooltip),
          onClick: getClickHandler(editor, formatName, nodesAffectedByInheritedStyles),
          getInitialActive: () => lastIsActive,
          nodesAffectedByInheritedStyles: nodesAffectedByInheritedStyles,
          isActive: isActive,
          disabled: getIsDisabled(disabled, formatName)
        }, rest));
      }
    });
    nodeChangeHandlerList.push(evt => ({
      [controlName]: getIsActive(formatName, evt)
    }));
  });
  const getFormattingAttributes = evt => nodeChangeHandlerList.reduce((acc, nodeChangeHandler) => Object.assign({}, acc, nodeChangeHandler(evt)), {});
  tinymce.canvas.addNodeChangeHandler(editor, {
    getFormattingAttributes
  });
}